import React from 'react';
import {
  Heart, Users, ShieldAlert, AlertCircle, Scale, Activity,
  Bell, ChevronRight, History, Zap, ListChecks, Clock, GraduationCap, ShieldCheck,
} from 'lucide-react';
import { DOMAINS, ROLES, APP_MODES } from '../../data/observationEngine';

const ICONS = {
  D1: Heart,
  D2: Users,
  D3: ShieldAlert,
  D4: AlertCircle,
  D5: Scale,
  D6: Activity,
};

/**
 * Home — "¿Qué observaste hoy?"
 * Muestra: header con saludo, toggle Completo/Rápido, link a Historial,
 * 6 tarjetas de dominio (§29).
 */
const ScreenHome = ({
  session, onSelectDomain, onOpenHistorial, draft, mode, onChangeMode,
  appMode = 'operativo', onRequestChangeAppMode,
  observacionesCount = 0,
}) => {
  const rolLabel = ROLES.find((r) => r.v === session?.rol)?.label || 'Observador/a';
  const firstName = (session?.nombre || '').split(' ')[0];

  return (
    <div className="step-enter flex flex-col h-full">
      <div className="px-5 pt-3 pb-4" data-testid="home-header">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold text-brand-muted">{rolLabel}</p>
            <h2 className="text-xl font-extrabold text-brand-deep" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              Hola{firstName ? `, ${firstName}` : ''}
            </h2>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="home-historial"
              onClick={onOpenHistorial}
              aria-label="Ver historial"
              className="tap-44 w-11 h-11 rounded-full bg-white border border-[#DDE6F7] flex items-center justify-center text-brand-deep hover:text-brand-violet hover:border-brand-violet relative"
            >
              <History className="w-5 h-5" />
              {observacionesCount > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-brand-violet text-white text-[10px] font-bold flex items-center justify-center" data-testid="home-historial-count">
                  {observacionesCount}
                </span>
              )}
            </button>
            <button
              data-testid="home-bell"
              aria-label="Notificaciones"
              className="tap-44 w-11 h-11 rounded-full bg-white border border-[#DDE6F7] flex items-center justify-center text-brand-deep relative"
            >
              <Bell className="w-5 h-5" />
              <span className="absolute top-1.5 right-1.5 w-1.5 h-1.5 rounded-full bg-[#E64545]" />
            </button>
          </div>
        </div>

        {/* CTA principal con modo toggle */}
        <div className="mt-5 rounded-2xl bg-[#EEF4FF] border border-[#DDE6F7] p-4">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p data-testid="home-step-label" className="text-[13px] font-bold text-brand-violet">
                Paso 1 de 5 – Dominio
              </p>
              <h3 className="mt-1.5 text-[22px] font-extrabold text-brand-deep leading-tight" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                ¿Qué observaste hoy?
              </h3>
            </div>
            {/* Mode toggle */}
            <div className="shrink-0 inline-flex bg-white rounded-full border border-[#DDE6F7] p-0.5 text-[11px] font-bold" role="group" aria-label="Modo de registro" data-testid="home-mode-toggle">
              <button
                data-testid="mode-completo"
                onClick={() => onChangeMode('completo')}
                aria-pressed={mode === 'completo'}
                className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full transition-colors ${
                  mode === 'completo' ? 'bg-brand-deep text-white' : 'text-brand-deep hover:text-brand-violet'
                }`}
              >
                <ListChecks className="w-3.5 h-3.5" /> Completo
              </button>
              <button
                data-testid="mode-rapido"
                onClick={() => onChangeMode('rapido')}
                aria-pressed={mode === 'rapido'}
                className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full transition-colors ${
                  mode === 'rapido' ? 'bg-brand-deep text-white' : 'text-brand-deep hover:text-brand-violet'
                }`}
              >
                <Zap className="w-3.5 h-3.5" /> Rápido
              </button>
            </div>
          </div>

          <p className="mt-2 text-xs text-brand-muted flex items-center gap-1.5" data-testid="home-eta-text">
            <Clock className="w-3 h-3" />
            {mode === 'rapido' ? 'Registro rápido · ≈ 25 segundos' : 'Registro ágil · ≈ 45 segundos'}
          </p>

          {/* Toggle Registro Oficial / Modo Inducción */}
          <div className="mt-3 inline-flex w-full bg-white rounded-full border border-[#DDE6F7] p-0.5 text-[11.5px] font-bold" role="group" aria-label="Modo de la base de datos" data-testid="home-appmode-toggle">
            <button
              data-testid="appmode-operativo"
              onClick={() => onRequestChangeAppMode?.('operativo')}
              aria-pressed={appMode === 'operativo'}
              className={`flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-full transition-colors ${
                appMode === 'operativo' ? 'text-white' : 'text-brand-deep hover:text-brand-violet'
              }`}
              style={appMode === 'operativo' ? { background: '#1E9F73' } : undefined}
            >
              <span aria-hidden>🟢</span>
              <ShieldCheck className="w-3.5 h-3.5" />
              {APP_MODES.operativo.label}
            </button>
            <button
              data-testid="appmode-induccion"
              onClick={() => onRequestChangeAppMode?.('induccion')}
              aria-pressed={appMode === 'induccion'}
              className={`flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-full transition-colors ${
                appMode === 'induccion' ? 'text-white' : 'text-brand-deep hover:text-brand-violet'
              }`}
              style={appMode === 'induccion' ? { background: '#C98718' } : undefined}
            >
              <span aria-hidden>🟡</span>
              <GraduationCap className="w-3.5 h-3.5" />
              {APP_MODES.induccion.label}
            </button>
          </div>

          <div className="progress-dots mt-3">
            <span className="is-active" />
            <span /><span /><span /><span />
          </div>
        </div>
      </div>

      <div className="px-5 pb-5 grid grid-cols-2 auto-rows-fr gap-3 flex-1" data-testid="home-domains-grid">
        {DOMAINS.map((d) => {
          const Icon = ICONS[d.id];
          return (
            <button
              key={d.id}
              data-testid={`home-domain-${d.id}`}
              onClick={() => onSelectDomain(d)}
              className="domain-card h-full flex flex-col"
              style={{
                background: '#FFFFFF',
                borderColor: '#F1F4FA',
                minHeight: 168,
              }}
            >
              <span aria-hidden className="absolute -top-3 -right-3 w-14 h-14 rounded-full z-0" style={{ background: d.soft, opacity: 0.7 }} />

              <div className="relative z-10">
                <span
                  className="domain-icon-pill"
                  style={{ background: d.bg, color: d.color }}
                >
                  <Icon className="w-5 h-5" />
                </span>
              </div>

              <h4
                className="relative z-10 mt-3 text-[14px] font-extrabold text-brand-deep leading-tight line-clamp-2"
                style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', minHeight: '2.4em' }}
              >
                {d.name}
              </h4>

              <p
                className="relative z-10 mt-1 text-[11.5px] text-brand-muted leading-snug line-clamp-2"
                style={{ minHeight: '2.6em' }}
              >
                {d.microcopy}
              </p>

              <span
                className="relative z-10 mt-auto pt-2 inline-flex items-center gap-0.5 text-[12px] font-bold text-brand-violet"
              >
                Registrar <ChevronRight className="w-3 h-3" />
              </span>
            </button>
          );
        })}
      </div>

      {draft?.dominio && (
        <div className="px-5 pb-4">
          <div className="rounded-2xl bg-[#FFF9EC] border border-[#FFE7C2] p-3 flex items-center justify-between" data-testid="home-draft">
            <div className="min-w-0">
              <p className="text-xs font-bold text-[#C98718]">Borrador guardado</p>
              <p className="text-xs text-brand-deep font-semibold truncate">{draft.dominio.name} · {draft.conducta?.label || 'sin conducta'}</p>
            </div>
            <button
              data-testid="home-resume-draft"
              onClick={() => onSelectDomain(draft.dominio)}
              className="text-xs font-extrabold text-[#C98718]"
            >
              Continuar
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScreenHome;
